/**
 * The top-bar bell: live unread badge + popover inbox. Mounts the realtime
 * subscription, so every AppShell page keeps the badge current without a
 * refresh (spec §12.5.5).
 */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, CheckCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils/cn'
import {
  useNotificationsInfiniteQuery,
  useUnreadCountQuery,
  useMarkReadMutation,
  useMarkAllReadMutation,
  useEventCatalogQuery,
  useNotificationsRealtime,
} from '../hooks/use-notifications'
import { configFor } from '../lib/notification-catalog'
import { NotificationItem } from './notification-item'
import type { NotificationRow } from '../api/notifications-api'

export function NotificationBell({ projectId }: { projectId?: string }) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  useNotificationsRealtime(projectId)

  const { data: unread = 0 } = useUnreadCountQuery(projectId)
  const { data: catalog } = useEventCatalogQuery()
  const feed = useNotificationsInfiniteQuery(projectId, { pageSize: 15 })
  const markRead = useMarkReadMutation(projectId)
  const markAll = useMarkAllReadMutation(projectId)

  const catalogLabels = useMemo(
    () => new Map((catalog ?? []).map((t) => [t.event_type, t.label_fa])),
    [catalog],
  )

  const items = feed.data?.pages[0] ?? []

  const openNotification = (n: NotificationRow) => {
    if (!n.is_read) markRead.mutate(n.id)
    setOpen(false)
    const route = configFor(n.event_type).route(n)
    if (route) navigate(route)
  }

  if (!projectId) {
    return (
      <Button variant="ghost" size="icon" className="text-muted-foreground" disabled>
        <Bell className="w-4 h-4" />
      </Button>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="اعلان‌ها"
          className={cn(
            'relative text-muted-foreground hover:text-foreground hover:bg-muted',
            unread > 0 && 'text-foreground',
          )}
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <Badge className="absolute -top-0.5 -end-0.5 h-4 min-w-4 px-0.5 flex items-center justify-center text-[10px] bg-destructive text-destructive-foreground border-0 rounded-full">
              {unread > 99 ? '99+' : unread}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" dir="rtl" className="w-96 p-0 overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <span className="text-sm font-semibold">اعلان‌ها</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
            disabled={unread === 0 || markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            <CheckCheck className="h-3.5 w-3.5" />
            خواندن همه
          </Button>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {feed.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-12 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
              <Bell className="h-6 w-6" />
              <span className="text-xs">اعلانی ندارید</span>
            </div>
          ) : (
            items.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                catalogLabelFa={catalogLabels.get(n.event_type)}
                onOpen={openNotification}
                compact
              />
            ))
          )}
        </div>

        <div className="border-t border-border">
          <Button
            variant="ghost"
            className="w-full rounded-none text-xs text-primary"
            onClick={() => {
              setOpen(false)
              navigate(`/projects/${projectId}/notifications`)
            }}
          >
            مشاهده همه اعلان‌ها
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
